import { useEffect } from 'react';
import { Center, Group, Loader, Stack } from '@mantine/core';
import { createFileRoute, Outlet, useNavigate, useRouterState } from '@tanstack/react-router';
import { useAuth } from '@workos-inc/authkit-react';
import { SignInUnavailable, WaitingApproval } from '~portal/components';
import { GlobalNav, GlobalNavMobileHeader } from '~portal/components/GlobalNav';
import { useSignInRedirect } from '~portal/hooks';
import {
  appMode,
  consumePendingReturnTo,
  isPlatform,
  pageScrollerProps,
  PageScrollers,
  shouldHoldForApproval,
} from '~portal/lib';
import * as styles from './route.css';

export const Route = createFileRoute('/_protected')({
  component: ProtectedLayout,
});

function ProtectedLayout() {
  const { isLoading, user, roles, permissions } = useAuth();
  const navigate = useNavigate();
  const returnTo = useRouterState({ select: (state) => state.location.href });
  const pathname = useRouterState({ select: (state) => state.location.pathname });
  const { failed, retry } = useSignInRedirect(!isLoading && !user, returnTo);

  // Deep-link restoration: the AuthKit roundtrip parks its returnTo (see lib/return-to.ts)
  // because onRedirectCallback's imperative navigate races router mount. Consumed exactly once,
  // only after auth has settled on a signed-in user.
  useEffect(() => {
    if (isLoading || !user) return;
    const pending = consumePendingReturnTo();
    if (pending && pending !== returnTo) {
      void navigate({ to: pending });
    }
  }, [isLoading, user, navigate, returnTo]);

  if (failed) return <SignInUnavailable onRetry={retry} />;

  if (isLoading || !user) {
    return (
      // The same frame as the signed-in shell, so the hand-over from spinner to app is not a
      // change of height — see `shell` in route.css.ts for why neither uses `100dvh`.
      <Center className={styles.shell} data-testid="protected-loading">
        <Loader size="lg" />
      </Center>
    );
  }

  // Evaluated only once AuthKit reports a signed-in user, by which point the claims it derives
  // from the access token have resolved — so the app never flashes before the gate decides.
  if (shouldHoldForApproval({ roles, permissions }, appMode)) {
    return <WaitingApproval />;
  }

  // The platform's global nav sits beside every page except the agent: the chat layout owns its
  // own sidebar (logo, threads, account), exactly as the Figma chat frame (`14:1450`) draws it.
  // The standalone build has no global nav at all — it is the chat, and nothing else.
  const withGlobalNav = isPlatform && !isChatPath(pathname);

  if (withGlobalNav) {
    return (
      <Group gap={0} align="stretch" wrap="nowrap" className={styles.shell}>
        <GlobalNav />
        <Stack gap={0} className={styles.content} {...pageScrollerProps(PageScrollers.Platform)}>
          <GlobalNavMobileHeader />
          <Outlet />
        </Stack>
      </Group>
    );
  }

  return (
    <Stack gap={0} className={styles.shell}>
      <Outlet />
    </Stack>
  );
}

const isChatPath = (path: string) => path === '/chat' || path.startsWith('/chat/');
